import { useEffect, useState } from "react";
import { CheckCircle2, Handshake, School } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import CourseCard from "@/components/CourseCard";
import EnquiryDialog from "@/components/EnquiryDialog";
import { Button } from "@/components/ui/button";

const MOU_PARTNERS = [
  { name: "DCB Girls College, Jorhat", note: "MoU signed for internships and skill development programs for students." },
  { name: "Nakachari College, Jorhat", note: "MoU signed for internships and various skill development programs." },
];

export default function Internship() {
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    api.get("/courses", { params: { category: "internships-workshops", limit: 30 } })
      .then((r) => setPrograms(r.data.items)).catch(() => {});
  }, []);

  const internships = programs.filter((p) => p.subcategory === "Internships");
  const workshops = programs.filter((p) => p.subcategory === "Workshops" || p.subcategory === "AI Awareness Programs");

  return (
    <div className="bg-navy-base min-h-screen" data-testid="internship-page">
      <SEO title="Internships & Workshops in Jorhat — AI, Cyber Security, Full Stack, Cloud | XTM TECHNOLOGIES"
        description="Online, offline and hybrid internships and workshops on Generative AI, Social Media Marketing, Cyber Security, Ethical Hacking, Full Stack Development, Python for Data Science and Cloud Computing at XTM FutureSkills Academy, Jorhat. MoU partner colleges: DCB Girls College and Nakachari College." />
      <section className="relative hero-glow overflow-hidden">
        <div className="absolute inset-0 cyber-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-400 mb-2">Internship Hub</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-4" data-testid="internship-heading">
            Internships & Workshops on <span className="text-gradient-orange">Trending Technologies</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-3xl leading-relaxed">
            Online, Offline and Hybrid programs in Generative AI & Productivity, Social Media Marketing, Cyber Security,
            Ethical Hacking, Full Stack Development, Python for Data Science, Cloud Computing and more — with mentor-guided
            projects and verifiable completion certificates.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="internship-programs">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-8">Internship Programs</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16" data-testid="internships-grid">
          {internships.map((c) => <CourseCard key={c.slug} course={c} />)}
        </div>

        <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-4">Workshops & AI Awareness Programs</h2>
        <p className="text-sm text-slate-400 max-w-3xl mb-8">
          We conduct both <strong className="text-slate-200">free and paid workshops</strong> and AI Awareness Programs at
          schools and colleges from time to time. Institutions can request a program for their campus.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16" data-testid="workshops-grid">
          {workshops.map((c) => <CourseCard key={c.slug} course={c} />)}
        </div>

        <div className="bg-card border border-white/10 rounded-2xl p-8 glow-card mb-16" data-testid="mou-section">
          <div className="flex items-center gap-3 mb-4">
            <Handshake className="w-7 h-7 text-orange-400" />
            <h2 className="font-display text-2xl font-bold text-white">College MoU Partners</h2>
          </div>
          <p className="text-sm text-slate-400 max-w-3xl mb-8">
            XTM TECHNOLOGIES has signed Memoranda of Understanding with the following institutions to provide
            internships and various skill development programs to their students.
          </p>
          <div className="grid sm:grid-cols-2 gap-5 mb-8">
            {MOU_PARTNERS.map((p) => (
              <div key={p.name} className="bg-navy-deep border border-orange-500/30 rounded-xl p-6" data-testid={`mou-${p.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                <div className="flex items-center gap-2.5 mb-2">
                  <School className="w-5 h-5 text-orange-400" />
                  <h3 className="font-display font-semibold text-white">{p.name}</h3>
                </div>
                <p className="text-sm text-slate-400">{p.note}</p>
              </div>
            ))}
          </div>
          <EnquiryDialog type="corporate" course="Institutional Program / MoU Collaboration" title="Request Institutional Program" trigger={
            <Button className="bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-full px-7 transition-colors" data-testid="institutional-request-btn">
              Request Institutional Program
            </Button>
          } />
        </div>

        <div className="bg-card border border-white/10 rounded-2xl p-8" data-testid="internship-process">
          <h2 className="font-display text-2xl font-bold text-white mb-6">How the Internship Works</h2>
          <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {["Registration", "Orientation", "Assignments", "Project Submission", "Mentor Feedback", "Certificate"].map((s) => (
              <div key={s} className="flex items-start gap-2 text-sm text-slate-300">
                <CheckCircle2 className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />{s}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
